import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Download, X } from 'lucide-react';
import { toast } from 'sonner';

interface AutomationJobImageGalleryProps {
  jobId: string;
  jobName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface GeneratedImage {
  id: string;
  image_url: string;
  storage_path: string | null;
  prompt: string;
  model: string;
  created_at: string;
}

const derivePublicUrl = (img: GeneratedImage) => {
  // Try deriving from storage_path first: "bucket/path/to/file.png"
  if (img.storage_path && img.storage_path.includes('/')) {
    const firstSlash = img.storage_path.indexOf('/');
    const bucket = img.storage_path.slice(0, firstSlash);
    const path = img.storage_path.slice(firstSlash + 1);
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl || img.image_url;
  }
  // Fallback: parse from existing image_url: .../object/public/{bucket}/{path}
  try {
    const url = new URL(img.image_url);
    const parts = url.pathname.split('/');
    const publicIdx = parts.findIndex((p) => p === 'public');
    if (publicIdx !== -1 && parts.length > publicIdx + 2) {
      const bucket = parts[publicIdx + 1];
      const path = parts.slice(publicIdx + 2).join('/');
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      return data.publicUrl || img.image_url;
    }
  } catch {}
  return img.image_url;
};
export const AutomationJobImageGallery = ({
  jobId,
  jobName,
  open,
  onOpenChange,
}: AutomationJobImageGalleryProps) => {
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);

  useEffect(() => {
    if (open) {
      loadImages();
    }
  }, [open, jobId]);

  const loadImages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('generated_images')
        .select('id, image_url, storage_path, prompt, model, created_at')
        .eq('automated_job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  // Realtime updates: reload images when new rows for this job are inserted
  useEffect(() => {
    if (!open) return;
    const channel = supabase
      .channel(`gallery-${jobId}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generated_images',
          filter: `automated_job_id=eq.${jobId}`
        },
        () => {
          loadImages();
        }
      )
      .subscribe();

    const interval = setInterval(() => {
      // Fallback polling in case realtime misses events
      loadImages();
    }, 20000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [open, jobId]);

  const downloadImage = async (image: GeneratedImage) => {
    try {
      const url = derivePublicUrl(image);
      const link = document.createElement('a');
      link.href = `${url}${url.includes('?') ? '&' : '?'}dl=${Date.now()}`;
      link.download = `${jobName}_${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Image downloaded');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-background/40 backdrop-blur-md border-border/50">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{jobName} - Generated Images ({images.length})</DialogTitle>
              <Button size="sm" variant="outline" onClick={loadImages}>
                Refresh
              </Button>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No images generated yet
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-border/50 bg-background/40 backdrop-blur-sm cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.image_url}
                    alt={image.prompt}
                    loading="lazy"
                    onError={(e) => {
                      // Attempt to repair broken links by deriving a fresh public URL
                      const fallback = derivePublicUrl(image);
                      if ((e.currentTarget as any).__triedFallback) {
                        e.currentTarget.src = '/placeholder.svg';
                        return;
                      }
                      (e.currentTarget as any).__triedFallback = true;
                      e.currentTarget.src = `${fallback}${fallback.includes('?') ? '&' : '?'}v=${Date.now()}`;
                    }}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(image);
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-xs text-white line-clamp-2">{image.prompt}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Size Image Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl p-0 bg-background/40 backdrop-blur-md border-border/50">
          {selectedImage && (
            <div className="relative">
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 right-2 z-10 bg-background/80 backdrop-blur-sm"
                onClick={() => setSelectedImage(null)}
              >
                <X className="h-4 w-4" />
              </Button>
              <img
                src={selectedImage.image_url}
                alt={selectedImage.prompt}
                className="w-full h-auto max-h-[85vh] object-contain"
                onError={(e) => {
                  const fallback = derivePublicUrl(selectedImage);
                  if ((e.currentTarget as any).__triedFallback) {
                    e.currentTarget.src = '/placeholder.svg';
                    return;
                  }
                  (e.currentTarget as any).__triedFallback = true;
                  e.currentTarget.src = `${fallback}${fallback.includes('?') ? '&' : '?'}v=${Date.now()}`;
                }}
              />
              <div className="p-4 bg-background/40 backdrop-blur-sm border-t border-border/50">
                <p className="text-sm font-medium mb-2">{selectedImage.prompt}</p>
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>Model: {selectedImage.model}</span>
                  <Button
                    size="sm"
                    onClick={() => downloadImage(selectedImage)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
